<template>
  <div class="wp-wrapper">

    <!-- Barra de pasos -->
    <Steps :size="totalSteps" :active="step" @step="goTo" />

    <!-- PASO 1: Datos básicos -->
    <div v-if="step === 1" class="wp-card">
      <h2 class="wp-card__title">Datos de la propiedad</h2>

      <div class="wp-field">
        <label class="wp-label">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
          Nombre de la propiedad
          <span class="wp-badge-opt">Opcional</span>
        </label>
        <input
          v-model="form.nombre"
          class="wp-input"
          type="text"
          placeholder="Ej: Edificio Central, Coliving Miraflores"
          maxlength="255"
        />
      </div>

      <div class="wp-field">
        <label class="wp-label">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          Tipo de propiedad
        </label>
        <div class="wp-radio-group">
          <label
            v-for="cat in categoriasDisponibles"
            :key="cat.value"
            class="wp-radio"
            :class="{ 'wp-radio--selected': form.categoria === cat.value }"
          >
            <input type="radio" :value="cat.value" v-model="form.categoria" />
            {{ cat.label }}
          </label>
        </div>
        <span v-if="errors.categoria" class="wp-error">{{ errors.categoria }}</span>
      </div>

      <div class="wp-field">
        <label class="wp-label">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          Dirección
        </label>
        <div v-if="isGoogleMapsLoaded">
          <vue-google-autocomplete
            id="propiedad-address"
            class="wp-input"
            placeholder="Busca la dirección en el mapa"
            v-on:placechanged="onPlaceChanged"
            v-model="form.direccion"
          />
        </div>
        <div v-else class="wp-hint">Cargando Google Maps...</div>
        <span v-if="errors.direccion" class="wp-error">{{ errors.direccion }}</span>
      </div>

      <div class="wp-field">
        <label class="wp-label">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          Total de unidades
        </label>
        <div class="wp-stepper">
          <button
            type="button"
            class="wp-stepper__btn"
            :disabled="form.total_unidades <= 2"
            @click="form.total_unidades = Math.max(2, form.total_unidades - 1)"
          >−</button>
          <span class="wp-stepper__val">{{ form.total_unidades }}</span>
          <button
            type="button"
            class="wp-stepper__btn"
            @click="form.total_unidades++"
          >+</button>
        </div>
        <span v-if="errors.total_unidades" class="wp-error">{{ errors.total_unidades }}</span>
      </div>

      <div class="wp-actions">
        <button class="wp-btn wp-btn--primary" @click="nextStep">Continuar</button>
      </div>
    </div>

    <!-- PASO 2: Fotos -->
    <div v-if="step === 2" class="wp-card">
      <h2 class="wp-card__title">Fotos de la propiedad</h2>
      <p class="wp-hint">Mínimo 5, máximo 12 fotos. Incluye fachada y espacios comunes.</p>

      <div class="wp-photo-add">
        <button
          class="wp-btn wp-btn--add-photo"
          type="button"
          :disabled="form.fotos.length >= 12"
          @click="triggerFileInput"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Agregar fotos
        </button>
      </div>

      <input
        ref="fileInput"
        type="file"
        multiple
        accept="image/png,image/jpeg,image/jpg"
        style="display:none"
        @change="onFilesSelected"
      />

      <p v-if="form.fotos.length > 0 && form.fotos.length < 5" class="wp-photo-msg wp-photo-msg--warn">
        Necesitás al menos 5 fotos para continuar. ({{ form.fotos.length }}/5)
      </p>
      <p v-if="form.fotos.length >= 12" class="wp-photo-msg wp-photo-msg--warn">
        Límite de 12 fotos alcanzado.
      </p>

      <div v-if="photoPreviews.length" class="wp-photos-grid">
        <div v-for="(preview, i) in photoPreviews" :key="i" class="wp-photo">
          <div class="wp-photo__inner">
            <img :src="preview" alt="preview" />
          </div>
          <button class="wp-photo__remove" @click.stop="removePhoto(i)" type="button">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      <div class="wp-actions">
        <button class="wp-btn wp-btn--secondary" @click="prevStep">Atrás</button>
        <button
          class="wp-btn wp-btn--primary"
          :disabled="form.fotos.length < 5"
          @click="nextStep"
        >Continuar</button>
      </div>
    </div>

    <!-- PASO 3: Cercanías -->
    <div v-if="step === 3" class="wp-card">
      <h2 class="wp-card__title">Cercanías y servicios de la propiedad</h2>
      <p class="wp-hint">Marca lo que está cerca de la propiedad.</p>

      <div class="wp-cerca-grid">
        <div
          v-for="item in cercaniaItems"
          :key="item.key"
          class="wp-cerca-item"
        >
          <img :src="require(`@/assets/alojamiento/${item.icon}.svg`)" class="wp-cerca-icon" />
          <label class="toggleS">
            <input
              class="toggleS__input"
              type="checkbox"
              v-model="form.cercanias[item.key]"
              :true-value="1"
              :false-value="0"
            />
            <span class="toggleS__label">
              <span class="toggleS__text">{{ item.label }}</span>
            </span>
          </label>
        </div>
      </div>

      <div class="wp-actions">
        <button class="wp-btn wp-btn--secondary" @click="prevStep">Atrás</button>
        <button class="wp-btn wp-btn--primary" @click="nextStep">Continuar</button>
      </div>
    </div>

    <!-- PASO 4: Sobre nosotros -->
    <div v-if="step === 4" class="wp-card">
      <h2 class="wp-card__title">Sobre nosotros</h2>
      <p class="wp-hint">Contá más sobre la propiedad y quién la gestiona.</p>

      <div class="wp-field">
        <label class="wp-label">
          Descripción de la propiedad
          <span class="wp-badge-opt">Opcional</span>
        </label>
        <textarea
          v-model="form.sobre_nosotros"
          class="wp-textarea"
          rows="6"
          placeholder="Ej: Somos un edificio familiar en el corazón de la ciudad. Ofrecemos espacios cómodos y bien equipados..."
        ></textarea>
      </div>

      <div class="wp-actions">
        <button class="wp-btn wp-btn--secondary" @click="prevStep">Atrás</button>
        <button class="wp-btn wp-btn--primary" @click="nextStep">Continuar</button>
      </div>
    </div>

    <!-- PASO 5: Normas de la casa -->
    <div v-if="step === 5" class="wp-card">
      <h2 class="wp-card__title">Normas de la casa</h2>
      <p class="wp-hint">Definí las reglas que se aplicarán a todas las unidades.</p>

      <div class="wp-field">
        <label class="wp-label">
          Reglas generales
          <span class="wp-badge-opt">Opcional</span>
        </label>
        <textarea
          v-model="form.reglas"
          class="wp-textarea"
          rows="4"
          placeholder="Ej: No fumar en áreas comunes, respetar horarios de silencio..."
        ></textarea>
      </div>

      <div class="wp-field">
        <label class="wp-label">
          Espacios comunes
          <span class="wp-badge-opt">Opcional</span>
        </label>
        <textarea
          v-model="form.espacios_comunes"
          class="wp-textarea"
          rows="3"
          placeholder="Ej: Sala de estar, lavandería, terraza..."
        ></textarea>
      </div>

      <div class="wp-field">
        <label class="wp-label">
          Uso de cocina
          <span class="wp-badge-opt">Opcional</span>
        </label>
        <textarea
          v-model="form.uso_cocina"
          class="wp-textarea"
          rows="3"
          placeholder="Ej: Cocina compartida disponible 24/7, cada inquilino tiene su espacio..."
        ></textarea>
      </div>

      <div class="wp-field">
        <label class="wp-label">
          Aseo y limpieza
          <span class="wp-badge-opt">Opcional</span>
        </label>
        <textarea
          v-model="form.aseo_limpieza"
          class="wp-textarea"
          rows="3"
          placeholder="Ej: Limpieza de áreas comunes semanal, cada inquilino limpia su unidad..."
        ></textarea>
      </div>

      <div class="wp-actions">
        <button class="wp-btn wp-btn--secondary" @click="prevStep">Atrás</button>
        <button class="wp-btn wp-btn--primary" @click="nextStep">Continuar</button>
      </div>
    </div>

    <!-- PASO 6: Confirmación -->
    <div v-if="step === 6" class="wp-card">
      <h2 class="wp-card__title">Confirma los datos de la propiedad</h2>

      <div class="wp-summary-grid">
        <div v-if="form.nombre" class="wp-summary-item">
          <span class="wp-summary-item__label">Nombre</span>
          <span class="wp-summary-item__value">{{ form.nombre }}</span>
        </div>
        <div class="wp-summary-item">
          <span class="wp-summary-item__label">Tipo</span>
          <span class="wp-summary-item__value">{{ form.categoria || '-' }}</span>
        </div>
        <div class="wp-summary-item wp-summary-item--full">
          <span class="wp-summary-item__label">Dirección</span>
          <span class="wp-summary-item__value">{{ form.direccion || '-' }}</span>
        </div>
        <div class="wp-summary-item">
          <span class="wp-summary-item__label">Unidades</span>
          <span class="wp-summary-item__value">{{ form.total_unidades }}</span>
        </div>
        <div class="wp-summary-item">
          <span class="wp-summary-item__label">Fotos cargadas</span>
          <span class="wp-summary-item__value">{{ form.fotos.length }}</span>
        </div>
      </div>

      <div class="wp-confirm-toggle">
        <label class="toggleS">
          <input class="toggleS__input" type="checkbox" v-model="accepted" />
          <span class="toggleS__label">
            <span class="toggleS__text">Confirmo que la información de la propiedad es correcta.</span>
          </span>
        </label>
      </div>

      <div v-if="submitError" class="wp-error wp-error--block">No se pudo crear la propiedad. Intentá nuevamente.</div>

      <div class="wp-actions">
        <button class="wp-btn wp-btn--secondary" @click="prevStep">Atrás</button>
        <button class="wp-btn wp-btn--primary" :disabled="!accepted || sending" @click="submit">
          <span v-if="sending" class="wp-spinner"></span>
          {{ sending ? 'Guardando…' : 'Crear propiedad' }}
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { mapMutations } from 'vuex';
import VueGoogleAutocomplete from 'vue-google-autocomplete';
import Steps from '@/components/Wizard/Steps.vue';

// Categorías según el tipo de flujo de carga
const CATEGORIAS_A = [
  { value: 'casa', label: 'Casa' },
  { value: 'hostal', label: 'Hostal' },
  { value: 'residencia', label: 'Residencia' },
  { value: 'lodge', label: 'Lodge' },
  { value: 'cabaña', label: 'Cabaña' },
  { value: 'refugio', label: 'Refugio' },
];

const CATEGORIAS_B = [
  { value: 'edificio', label: 'Edificio' },
  { value: 'coliving', label: 'Coliving' },
  { value: 'complejo', label: 'Complejo' },
  { value: 'portfolio', label: 'Portfolio (distintas propiedades)' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'residencia', label: 'Residencia' },
];

export default {
  name: 'WizardPropiedad',
  components: { VueGoogleAutocomplete, Steps },
  data() {
    return {
      step: 1,
      totalSteps: 6,
      stepLabels: ['Datos', 'Fotos', 'Cercanías', 'Sobre nosotros', 'Normas', 'Confirmar'],
      accepted: false,
      sending: false,
      submitError: false,
      isGoogleMapsLoaded: false,
      errors: {
        categoria: null,
        direccion: null,
        total_unidades: null,
      },
      form: {
        nombre: '',
        categoria: '',
        direccion: '',
        latitud: null,
        longitud: null,
        total_unidades: 2,
        fotos: [],
        cercanias: {
          paradero_micro: 0,
          estacion_metro: 0,
          hospital: 0,
          supermercado: 0,
          banco: 0,
          zona_nocturna: 0,
          areas_verdes: 0,
          centro_comercial: 0,
          centro_civico: 0,
          gimnasio_externo: 0,
        },
        sobre_nosotros: '',
        reglas: '',
        espacios_comunes: '',
        uso_cocina: '',
        aseo_limpieza: '',
      },
      photoPreviews: [],
      cercaniaItems: [
        { key: 'paradero_micro', label: 'Paradero de micro', icon: 'paradero-buses' },
        { key: 'estacion_metro', label: 'Estación de metro', icon: 'estacion-metro' },
        { key: 'hospital', label: 'Hospital', icon: 'hospital' },
        { key: 'supermercado', label: 'Supermercado', icon: 'supermercado' },
        { key: 'banco', label: 'Banco / ATM', icon: 'banco' },
        { key: 'zona_nocturna', label: 'Zona nocturna', icon: 'zona-nocturna' },
        { key: 'areas_verdes', label: 'Áreas verdes', icon: 'areas-verdes' },
        { key: 'centro_comercial', label: 'Centro comercial', icon: 'centro-comercial' },
        { key: 'centro_civico', label: 'Centro cívico', icon: 'centro-civico' },
        { key: 'gimnasio_externo', label: 'Gimnasio cercano', icon: 'gimnasio' },
      ],
    };
  },
  computed: {
    opcion() {
      return this.$route.query.opcion || 'A';
    },
    categoriasDisponibles() {
      return this.opcion === 'B' ? CATEGORIAS_B : CATEGORIAS_A;
    },
  },
  methods: {
    ...mapMutations([
      'setPropiedadActiva',
      'setTotalUnidadesPropiedad',
      'setUnidadesCargadasPropiedad',
      'setTipoCarga',
    ]),
    nextStep() {
      if (this.step === 1 && !this.validateStep1()) return;
      if (this.step === 2 && this.form.fotos.length < 5) return;
      this.step += 1;
      window.scrollTo(0, 0);
    },
    prevStep() {
      if (this.step > 1) {
        this.step -= 1;
        window.scrollTo(0, 0);
      }
    },
    goTo(step) {
      if (step >= 1 && step <= this.step) {
        this.step = step;
        window.scrollTo(0, 0);
      }
    },
    validateStep1() {
      this.errors.categoria = null;
      this.errors.direccion = null;
      this.errors.total_unidades = null;

      if (!this.form.categoria) {
        this.errors.categoria = 'Selecciona una categoría.';
      }
      if (!this.form.direccion || this.form.latitud === null || this.form.longitud === null) {
        this.errors.direccion = 'Selecciona una dirección válida desde el autocompletado.';
      }
      if (!this.form.total_unidades || Number(this.form.total_unidades) < 2) {
        this.errors.total_unidades = 'Indica al menos 2 unidades.';
      }

      return !this.errors.categoria && !this.errors.direccion && !this.errors.total_unidades;
    },
    onPlaceChanged(addressData, placeResultData) {
      if (!placeResultData || !placeResultData.geometry) return;
      this.form.latitud = placeResultData.geometry.location.lat();
      this.form.longitud = placeResultData.geometry.location.lng();
      this.form.direccion = placeResultData.formatted_address || this.form.direccion;
      this.errors.direccion = null;
    },
    triggerFileInput() {
      if (this.form.fotos.length >= 12) return;
      this.$refs.fileInput.click();
    },
    onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      const available = 12 - this.form.fotos.length;
      files.slice(0, available).forEach((file) => {
        this.form.fotos.push(file);
        this.photoPreviews.push(URL.createObjectURL(file));
      });
      event.target.value = '';
    },
    removePhoto(index) {
      const preview = this.photoPreviews[index];
      if (preview) URL.revokeObjectURL(preview);
      this.photoPreviews.splice(index, 1);
      this.form.fotos.splice(index, 1);
    },
    async submit() {
      if (!this.validateStep1()) { this.step = 1; window.scrollTo(0, 0); return; }
      if (this.form.fotos.length < 5) { this.step = 2; window.scrollTo(0, 0); return; }

      this.sending = true;
      this.submitError = false;

      try {
        const fd = new FormData();
        if (this.form.nombre) fd.append('nombre', this.form.nombre);
        fd.append('categoria', this.form.categoria);
        fd.append('direccion', this.form.direccion);
        fd.append('latitud', this.form.latitud);
        fd.append('longitud', this.form.longitud);
        fd.append('total_unidades', this.form.total_unidades);

        Object.keys(this.form.cercanias).forEach((field) => {
          fd.append(field, this.form.cercanias[field] ? 1 : 0);
        });

        // Sobre nosotros y normas
        if (this.form.sobre_nosotros) fd.append('sobre_nosotros', this.form.sobre_nosotros);
        if (this.form.reglas) fd.append('reglas', this.form.reglas);
        if (this.form.espacios_comunes) fd.append('espacios_comunes', this.form.espacios_comunes);
        if (this.form.uso_cocina) fd.append('uso_cocina', this.form.uso_cocina);
        if (this.form.aseo_limpieza) fd.append('aseo_limpieza', this.form.aseo_limpieza);

        this.form.fotos.forEach((file) => fd.append('fotos[]', file));

        const response = await axios.post(
          process.env.VUE_APP_API_URL + 'properties/create',
          fd
        );

        if (response && response.status === 201 && response.data && response.data.idpropiedad) {
          this.setTipoCarga('propiedad');
          this.setPropiedadActiva(response.data.idpropiedad);
          this.setTotalUnidadesPropiedad(Number(this.form.total_unidades));
          this.setUnidadesCargadasPropiedad(0);

          localStorage.setItem('propiedad_activa', String(response.data.idpropiedad));
          localStorage.setItem('total_unidades_propiedad', String(this.form.total_unidades));
          localStorage.setItem('unidades_cargadas_propiedad', '0');

          this.$router.push('/host/cargar-unidades');
        }
      } catch (error) {
        console.error('Error al crear propiedad', error);
        this.submitError = true;
      } finally {
        this.sending = false;
      }
    },
  },
  mounted() {
    window.googleMapsLoaded.then(() => {
      this.isGoogleMapsLoaded = true;
    }).catch(() => {
      console.error('Error al cargar Google Maps.');
    });
  },
  beforeDestroy() {
    this.photoPreviews.forEach((p) => URL.revokeObjectURL(p));
  },
};
</script>

<style scoped lang="scss">
// ── Paleta naranja ────────────────────────────────────────────────────────
$orange:        #E8703A;
$orange-dark:   #C45A28;
$orange-light:  #FEF3EC;
$orange-title:  #7A2E0E;
$orange-border: #F5B08A;

.wp-wrapper {
  max-width: 860px;
  margin: 0 auto;
  padding: 2.5rem 1rem;
}

// ── Card ─────────────────────────────────────────────────────────────────
.wp-card {
  background: #fff;
  border: 1px solid #ede9e4;
  border-radius: 14px;
  padding: 1.75rem 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-top: 2rem;
}

.wp-card__title {
  font-size: 17px;
  font-weight: 600;
  color: $orange-title;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1.5px solid $orange-light;
}

// ── Campos ──────────────────────────────────────────────────────────────
.wp-field {
  margin-bottom: 1.25rem;
}

.wp-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #5F5E5A;
  margin-bottom: 8px;

  svg {
    color: $orange;
    flex-shrink: 0;
  }
}

.wp-badge-opt {
  margin-left: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $orange;
  background: $orange-light;
  border: 1px solid $orange-border;
  border-radius: 20px;
  padding: 1px 7px;
}

.wp-hint {
  font-size: 12px;
  color: #a0a09a;
}

// ── Stepper ───────────────────────────────────────────────────────
.wp-stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dddbd5;
  border-radius: 10px;
  overflow: hidden;
  height: 40px;

  &__btn {
    width: 42px;
    height: 100%;
    border: none;
    background: #f7f5f2;
    font-size: 18px;
    font-weight: 400;
    color: #5F5E5A;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    line-height: 1;

    &:hover:not(:disabled) {
      background: $orange-light;
      color: $orange;
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  &__val {
    min-width: 56px;
    text-align: center;
    font-size: 17px;
    font-weight: 600;
    color: #1a1918;
    border-left: 1px solid #dddbd5;
    border-right: 1px solid #dddbd5;
    padding: 0 8px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.wp-input {
  width: 100%;
  height: 38px;
  padding: 0 11px;
  border: 1px solid #dddbd5;
  border-radius: 9px;
  font-size: 14px;
  outline: none;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    border-color: $orange;
    box-shadow: 0 0 0 3px rgba($orange, 0.14);
  }
}

.wp-textarea {
  width: 100%;
  padding: 10px 11px;
  border: 1px solid #dddbd5;
  border-radius: 9px;
  font-size: 14px;
  outline: none;
  background: #fff;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    border-color: $orange;
    box-shadow: 0 0 0 3px rgba($orange, 0.14);
  }
}

.wp-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

// ── Radio de categoría ───────────────────────────────────────────────────
.wp-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wp-radio {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 13px;
  border: 1px solid #dddbd5;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;

  input { display: none; }

  &:hover:not(.wp-radio--selected) {
    border-color: $orange-border;
    background: #fdf8f5;
  }

  &--selected {
    border-color: $orange;
    background: $orange-light;
    color: $orange-title;
    font-weight: 500;
  }
}

// ── Dropzone / Fotos ─────────────────────────────────────────────────────
.wp-photo-add {
  text-align: center;
  margin: 1.25rem 0 0.75rem;
}

.wp-btn--add-photo {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 26px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: $orange;
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba($orange, 0.30);
  transition: background 0.18s, transform 0.12s, box-shadow 0.18s;

  &:hover:not(:disabled) {
    background: $orange-dark;
    box-shadow: 0 4px 14px rgba($orange, 0.38);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.wp-photo-msg {
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  margin: 0.5rem 0 0.75rem;

  &--warn { color: #D85A30; }
}

// ── Grid de fotos ────────────────────────────────────────────────────────
.wp-photos-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin: 1rem 0;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.wp-photo {
  position: relative;
  padding: 6px;

  &__inner {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 150px;
    overflow: hidden;
    border: 1.5px dashed #dddbd5;
    border-radius: 12px;
    padding: 6px;
    transition: border-color 0.15s;

    &:hover { border-color: $orange-border; }

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 8px;
    }
  }

  &__remove {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 26px;
    height: 26px;
    border: 2px solid #e53e3e;
    border-radius: 50%;
    background: #fff;
    color: #e53e3e;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: #e53e3e;
      color: #fff;
    }
  }
}

// ── Cercanías ─────────────────────────────────────────────────────────────
.wp-cerca-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 0;
  margin: 1rem 0 1.5rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
}

.wp-cerca-item {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
  padding: 10px 0;
}

.wp-cerca-icon {
  width: 36px;
  margin-right: 10px;
  flex-shrink: 0;
}

// ── Resumen ──────────────────────────────────────────────────────────────
.wp-summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 0 0 1.75rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
}

.wp-summary-item {
  background: $orange-light;
  border: 1px solid $orange-border;
  border-radius: 10px;
  padding: 14px 16px;

  &--full {
    grid-column: 1 / -1;
  }

  &__label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $orange;
    margin-bottom: 5px;
  }

  &__value {
    display: block;
    font-size: 15px;
    font-weight: 500;
    color: #1a1918;
  }
}

.wp-confirm-toggle {
  margin-bottom: 1.5rem;
}

// ── Errores ──────────────────────────────────────────────────────────────
.wp-error {
  display: block;
  font-size: 12px;
  color: #D85A30;
  margin-top: 4px;

  &--block {
    background: #fef0ec;
    border: 1px solid #D85A30;
    border-radius: 7px;
    padding: 8px 12px;
    margin-bottom: 1rem;
  }
}

// ── Acciones ─────────────────────────────────────────────────────────────
.wp-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid #f5f3ef;
}

.wp-btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: background 0.18s, transform 0.12s, box-shadow 0.18s;

  &--primary {
    background: $orange;
    color: #fff;
    border: none;
    box-shadow: 0 2px 8px rgba($orange, 0.30);

    &:hover:not(:disabled) {
      background: $orange-dark;
      box-shadow: 0 4px 14px rgba($orange, 0.38);
      transform: translateY(-1px);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 1px 5px rgba($orange, 0.25);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &--secondary {
    background: transparent;
    color: #5F5E5A;
    border: 1px solid #dddbd5;

    &:hover {
      background: #f7f5f2;
      border-color: #c8c7c2;
    }
  }
}

// ── Spinner inline ────────────────────────────────────────────────────────
.wp-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: wp-spin 0.6s linear infinite;
}

@keyframes wp-spin {
  to { transform: rotate(360deg); }
}
</style>
